import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import app from '@infrastructure/firebase/FirebaseConfig';
import { notificationDomain } from '@domain/notification/NotificationDomainFactory';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

/**
 * Đăng ký Service Worker FCM (bắt buộc trước khi getToken).
 */
export async function registerFCMServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.warn('[FCM] Đăng ký Service Worker thất bại:', err);
    return null;
  }
}

/**
 * Lấy FCM token (chỉ chạy trên client, sau khi permission granted).
 */
export async function getFCMToken(opts = {}) {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  const vapidKey = opts.vapidKey || VAPID_KEY;
  if (!vapidKey) {
    console.warn('[FCM] VITE_FIREBASE_VAPID_KEY chưa cấu hình.');
    return null;
  }
  try {
    await registerFCMServiceWorker();
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey });
    return token || null;
  } catch (err) {
    if (err.code === 'messaging/permission-blocked') return null;
    console.error('[FCM] getToken error:', err);
    return null;
  }
}

/**
 * Yêu cầu quyền thông báo.
 */
export async function requestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Lưu FCM token vào Supabase.
 */
export async function saveTokenToFirestore(userId, token, opts = {}) {
  if (!userId || !token) return;
  const useCase = notificationDomain.getSaveFcmTokenUseCase();
  await useCase.execute(userId, {
    token,
    userAgent: opts.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  });
}

/**
 * Bật thông báo trên thiết bị hiện tại.
 */
export async function enablePushNotifications(userId) {
  if (!userId) return { success: false, message: 'Chưa đăng nhập.' };
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { success: false, message: 'Bạn chưa cho phép thông báo.' };
  }
  const token = await getFCMToken();
  if (!token) {
    return { success: false, message: 'Không lấy được token.' };
  }
  try {
    await saveTokenToFirestore(userId, token);
    return { success: true, message: 'Đã bật thông báo trên thiết bị này.' };
  } catch (err) {
    console.error('[FCM] saveToken error:', err);
    return { success: false, message: 'Lưu token thất bại: ' + (err.message || err) };
  }
}

/**
 * Đăng ký listener tin nhắn khi app đang mở (foreground).
 */
export function onForegroundMessage(callback) {
  if (typeof window === 'undefined') return () => {};
  try {
    const messaging = getMessaging(app);
    return onMessage(messaging, callback);
  } catch (err) {
    console.warn('[FCM] onMessage not available:', err);
    return () => {};
  }
}

/**
 * Ghi yêu cầu gửi thông báo (Backend/Edge Function sẽ gửi FCM).
 */
export async function enqueueNotificationRequest(userId, type, payload) {
  if (!userId) return;
  try {
    const useCase = notificationDomain.getEnqueueNotificationUseCase();
    await useCase.execute(userId, type, payload);
  } catch (err) {
    console.warn('[FCM] enqueueNotificationRequest error:', err);
  }
}

export default {
  registerFCMServiceWorker,
  getFCMToken,
  requestNotificationPermission,
  saveTokenToFirestore,
  enablePushNotifications,
  onForegroundMessage,
  enqueueNotificationRequest
};

